import { useState, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { fDateTime } from "@/lib/format";
import { GraduationCap, Plus, Users, Trash2, CheckCircle2, ExternalLink, MapPin, Calendar, Link2 } from "lucide-react";
import { toast } from "sonner";

const AUD = [
  { v: "all", tk: "aud_all" }, { v: "business_area", tk: "aud_area" }, { v: "department", tk: "aud_dept" },
  { v: "team", tk: "aud_team" }, { v: "shift", tk: "aud_shift" }, { v: "individual", tk: "aud_individual" },
];

function CreateForm({ competences, refs, onCreated }) {
  const { t } = useLang();
  const [f, setF] = useState({
    title: "", description: "", type: "e_learning", url: "", competence_id: "", target_level: 3,
    validity: "one_time", audienceType: "all", audienceId: "", post_to_feed: false,
    enrollment_mode: "assign", session_date: "", location: "", capacity: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const opts = f.audienceType === "business_area" ? refs.businessAreas : f.audienceType === "department" ? refs.departments
    : f.audienceType === "team" ? refs.teams : f.audienceType === "shift" ? refs.shifts
    : f.audienceType === "individual" ? refs.employees : [];
  const create = async () => {
    if (!f.title.trim()) return toast.error(t("enter_title"));
    const audience = f.audienceType === "all" ? { type: "all" } : { type: f.audienceType, ids: f.audienceId ? [f.audienceId] : [] };
    try {
      await api.post("/training", {
        title: f.title, description: f.description, type: f.type, url: f.url || null,
        competence_id: f.competence_id || null, target_level: Number(f.target_level), validity: f.validity,
        audience, post_to_feed: f.post_to_feed, enrollment_mode: f.enrollment_mode,
        session_date: f.session_date || null, location: f.location || null,
        capacity: f.capacity ? Number(f.capacity) : null,
      });
      toast.success(t("tr_created"));
      setF({ title: "", description: "", type: "e_learning", url: "", competence_id: "", target_level: 3, validity: "one_time", audienceType: "all", audienceId: "", post_to_feed: false, enrollment_mode: "assign", session_date: "", location: "", capacity: "" });
      onCreated();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const optName = (o) => o.name || `${o.first_name || ""} ${o.last_name || ""}`.trim();

  return (
    <Card className="mb-6 grid gap-3 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label>{t("title_lbl")}</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="tr-title" /></div>
        <div><Label>{t("type_lbl")}</Label>
          <Select value={f.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="tr-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="e_learning">{t("tr_e")}</SelectItem><SelectItem value="physical">{t("tr_physical")}</SelectItem></SelectContent>
          </Select></div>
      </div>
      <div><Label>{t("desc_lbl")}</Label><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="tr-desc" /></div>

      {f.type === "e_learning" && (
        <div><Label>{t("tr_url")}</Label><Input value={f.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" data-testid="tr-url" /></div>
      )}
      {f.type === "physical" && (
        <div className="grid gap-3 sm:grid-cols-4">
          <div><Label>{t("tr_enroll")}</Label>
            <Select value={f.enrollment_mode} onValueChange={(v) => set("enrollment_mode", v)}>
              <SelectTrigger data-testid="tr-enroll"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="assign">{t("tr_assign_mode")}</SelectItem><SelectItem value="self">{t("tr_self_mode")}</SelectItem></SelectContent>
            </Select></div>
          <div><Label>{t("tr_session_date")}</Label><Input type="datetime-local" value={f.session_date} onChange={(e) => set("session_date", e.target.value)} data-testid="tr-date" /></div>
          <div><Label>{t("tr_location")}</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="tr-location" /></div>
          <div><Label>{t("tr_capacity")}</Label><Input type="number" min="1" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="tr-capacity" /></div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>{t("tr_competence")}</Label>
          <Select value={f.competence_id || "none"} onValueChange={(v) => set("competence_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="tr-competence"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="none">—</SelectItem>{(competences || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{t("tr_target_level")}</Label>
          <Select value={String(f.target_level)} onValueChange={(v) => set("target_level", v)}>
            <SelectTrigger data-testid="tr-level"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{t("lvl", { n })}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{t("tr_validity")}</Label>
          <Select value={f.validity} onValueChange={(v) => set("validity", v)}>
            <SelectTrigger data-testid="tr-validity"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="one_time">{t("tr_one_time")}</SelectItem><SelectItem value="annual">{t("tr_annual")}</SelectItem></SelectContent>
          </Select></div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Label>{t("sv_audience")}</Label>
          <Select value={f.audienceType} onValueChange={(v) => setF((s) => ({ ...s, audienceType: v, audienceId: "" }))}>
            <SelectTrigger className="w-44" data-testid="tr-aud-type"><SelectValue /></SelectTrigger>
            <SelectContent>{AUD.map((o) => <SelectItem key={o.v} value={o.v}>{t(o.tk)}</SelectItem>)}</SelectContent>
          </Select></div>
        {f.audienceType !== "all" && (
          <Select value={f.audienceId} onValueChange={(v) => set("audienceId", v)}>
            <SelectTrigger className="w-52" data-testid="tr-aud-target"><SelectValue placeholder={t("choose")} /></SelectTrigger>
            <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{optName(o)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <label className="flex items-center gap-2 text-sm" data-testid="tr-post-feed">
          <Checkbox checked={f.post_to_feed} onCheckedChange={(v) => set("post_to_feed", !!v)} /> {t("tr_post_feed")}
        </label>
        <Button className="ml-auto" onClick={create} data-testid="tr-create"><Plus className="mr-1 h-4 w-4" />{t("tr_create")}</Button>
      </div>
    </Card>
  );
}

function Participants({ training, employees, onChanged }) {
  const { t } = useLang();
  const [list, setList] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [sel, setSel] = useState({});
  const load = () => api.get(`/training/${training.id}/participants`).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [training.id]);
  const enrolledIds = new Set((list || []).map((p) => p.employee_id));
  const assign = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (ids.length === 0) return setAssignOpen(false);
    try { await api.post(`/training/${training.id}/assign`, { employee_ids: ids }); setAssignOpen(false); setSel({}); load(); onChanged && onChanged(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-3 border-t pt-3" data-testid={`participants-${training.id}`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold">{t("tr_participants")}</span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setAssignOpen(true)} data-testid={`assign-btn-${training.id}`}>
          <Plus className="mr-1 h-4 w-4" />{t("tr_assign_btn")}
        </Button>
      </div>
      <div className="space-y-1">
        {(list || []).length === 0 && <p className="text-xs text-muted-foreground">{t("no_apps")}</p>}
        {(list || []).map((p) => (
          <div key={p.employee_id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">{p.employee_name}</span>
            {p.status === "completed"
              ? <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" />{t("tr_status_completed")}</Badge>
              : <Badge variant="secondary">{t("tr_status_registered")}</Badge>}
          </div>
        ))}
      </div>
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("tr_assign_title")}</DialogTitle></DialogHeader>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {(employees || []).map((e) => (
              <label key={e.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary/50">
                <Checkbox checked={!!sel[e.id] || enrolledIds.has(e.id)} disabled={enrolledIds.has(e.id)}
                  onCheckedChange={(v) => setSel((s) => ({ ...s, [e.id]: !!v }))} data-testid={`assign-emp-${e.id}`} />
                {e.first_name} {e.last_name}{enrolledIds.has(e.id) && <span className="text-xs text-muted-foreground">· {t("tr_status_registered")}</span>}
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={assign} data-testid="assign-save">{t("tr_assign_save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManageView() {
  const { t } = useLang();
  const { data: trainings, reload } = useApi("/training");
  const { data: competences } = useApi("/competences");
  const { data: employees } = useApi("/employees");
  const { data: departments } = useApi("/departments");
  const { data: teams } = useApi("/teams");
  const { data: shifts } = useApi("/shifts");
  const { data: businessAreas } = useApi("/business_areas");
  const refs = { departments, teams, shifts, businessAreas, employees };
  const [open, setOpen] = useState(null);
  const cName = (id) => (competences || []).find((c) => c.id === id)?.name;

  const toggle = async (tr) => { try { await api.patch(`/training/${tr.id}`, { status: tr.status === "published" ? "closed" : "published" }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { if (!window.confirm(t("tr_confirm_delete"))) return; try { await api.delete(`/training/${id}`); reload(); toast.success(t("removed")); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <PageHeader title={t("tr_title")} icon={GraduationCap} subtitle={t("tr_sub_manage")} />
      <CreateForm competences={competences} refs={refs} onCreated={reload} />
      {(trainings || []).length === 0 ? <EmptyState icon={GraduationCap} title={t("tr_empty_manage_title")} text={t("tr_empty_manage_text")} /> : (
        <div className="space-y-3">
          {trainings.map((tr) => (
            <Card key={tr.id} className="p-4" data-testid={`training-${tr.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <GraduationCap className="h-4 w-4 text-accent" />
                <span className="font-medium">{tr.title}</span>
                <Badge variant="outline">{tr.type === "physical" ? t("tr_physical") : t("tr_e")}</Badge>
                {tr.validity === "annual" && <Badge variant="secondary">{t("tr_annual")}</Badge>}
                {tr.status !== "published" && <Badge variant="secondary">{t("sv_closed")}</Badge>}
                <span className="ml-auto text-xs text-muted-foreground">{tr.completed_count} {t("tr_completed_w")} · {tr.registered_count} {t("tr_registered")}</span>
              </div>
              {tr.description && <p className="mt-1 text-sm text-muted-foreground">{tr.description}</p>}
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                {tr.competence_id && <span>{t("tr_competence")}: {cName(tr.competence_id)} ({t("lvl", { n: tr.target_level })})</span>}
                {tr.type === "physical" && tr.session_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fDateTime(tr.session_date)}</span>}
                {tr.type === "physical" && tr.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{tr.location}</span>}
                {tr.type === "physical" && tr.capacity && <span>{t("tr_seats", { n: tr.capacity })}</span>}
                {tr.url && <a href={tr.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-accent underline"><Link2 className="h-3 w-3" />{t("tr_url")}</a>}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpen((o) => (o === tr.id ? null : tr.id))} data-testid={`show-part-${tr.id}`}>
                  <Users className="mr-1 h-4 w-4" />{t("tr_participants")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(tr)} data-testid={`toggle-tr-${tr.id}`}>{tr.status === "published" ? t("close_btn") : t("open_btn")}</Button>
                <Button size="sm" variant="ghost" onClick={() => del(tr.id)} data-testid={`del-tr-${tr.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
              {open === tr.id && <Participants training={tr} employees={employees} onChanged={reload} />}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useLang();
  if (status === "completed") return <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" />{t("tr_status_completed")}</Badge>;
  if (status === "registered") return <Badge variant="secondary">{t("tr_status_registered")}</Badge>;
  if (status === "expired") return <Badge variant="destructive">{t("tr_status_expired")}</Badge>;
  return <Badge variant="outline">{t("tr_status_none")}</Badge>;
}

function EmployeeView() {
  const { t } = useLang();
  const { data: mine, reload } = useApi("/training/mine");
  const { data: competences } = useApi("/competences");
  const cName = (id) => (competences || []).find((c) => c.id === id)?.name;
  const register = async (id) => { try { await api.post(`/training/${id}/register`); toast.success(t("tr_reg_toast")); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const complete = async (id) => { try { await api.post(`/training/${id}/complete`); toast.success(t("tr_done_toast")); reload(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t("tr_title")} icon={GraduationCap} subtitle={t("tr_sub_emp")} />
      {(mine || []).length === 0 ? <EmptyState icon={GraduationCap} title={t("tr_empty_emp_title")} text={t("tr_empty_emp_text")} /> : (
        <div className="space-y-3">
          {mine.map((tr) => {
            const done = tr.my_status === "completed";
            const needsAction = tr.my_status === "none" || tr.my_status === "expired" || tr.my_status === "registered";
            const isPhysSelf = tr.type === "physical" && tr.enrollment_mode === "self";
            return (
              <Card key={tr.id} className="p-4" data-testid={`mytraining-${tr.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-accent" />
                  <span className="font-medium">{tr.title}</span>
                  <Badge variant="outline">{tr.type === "physical" ? t("tr_physical") : t("tr_e")}</Badge>
                  <span className="ml-auto"><StatusBadge status={tr.my_status} /></span>
                </div>
                {tr.description && <p className="mt-1 text-sm text-muted-foreground">{tr.description}</p>}
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {tr.competence_id && <span>{cName(tr.competence_id)} ({t("lvl", { n: tr.target_level })})</span>}
                  {tr.type === "physical" && tr.session_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fDateTime(tr.session_date)}</span>}
                  {tr.type === "physical" && tr.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{tr.location}</span>}
                  {done && tr.valid_until && <span>{t("tr_valid_until", { d: fDateTime(tr.valid_until) })}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tr.type === "e_learning" && tr.url && (
                    <a href={tr.url} target="_blank" rel="noreferrer" data-testid={`open-${tr.id}`}
                      className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-secondary"><ExternalLink className="mr-1 h-4 w-4" />{t("tr_open")}</a>
                  )}
                  {isPhysSelf && tr.my_status === "none" && (
                    <Button size="sm" variant="outline" onClick={() => register(tr.id)} data-testid={`register-${tr.id}`}>{t("tr_register")}</Button>
                  )}
                  {needsAction && (
                    <Button size="sm" onClick={() => complete(tr.id)} data-testid={`complete-${tr.id}`}>
                      <CheckCircle2 className="mr-1 h-4 w-4" />{tr.my_status === "expired" ? t("tr_retake") : t("tr_mark_done")}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Utbildningar() {
  const { effectiveRole } = useAuth();
  const canManage = ["owner", "hr_admin"].includes(effectiveRole);
  return canManage ? <ManageView /> : <EmployeeView />;
}
